import { AuthorizationsFilter, AuthorizationTransition } from '../models';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useState, useCallback } from 'react';
import { IconButton } from '@mui/material';
import { useSortableData } from '@/hooks';
import { Table } from '@/components';
import {
  useAuthorizations,
  useAuthorizationsPagination,
  useAuthorizationsTable,
} from '../hooks';
import { AuthorizationReasonModal } from './index';

type AuthorizationsTableProps = {
  authorizationsFilter?: AuthorizationsFilter;
};

const AuthorizationsTable = ({
  authorizationsFilter,
}: AuthorizationsTableProps) => {
  const navigate = useNavigate();
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const handleReasonClick = useCallback((reason: string) => setSelectedReason(reason), []);

  const { currentPage, pageSize, handlePageChange, handlePageSizeChange } =
    useAuthorizationsPagination();

  const { authorizations, isAuthorizationsLoading, totalItems, totalPages } =
    useAuthorizations({
      paginated: {
        page: currentPage,
        pageSize: pageSize,
      },
      filters: authorizationsFilter,
    });

  const { columns } = useAuthorizationsTable(handleReasonClick);
  const { sortedData, selectedSort, handleSort } = useSortableData({
    data: authorizations,
    currentPage,
  });

  const renderActions = (rowData: Record<string, unknown>) => {
    const auth = rowData as unknown as AuthorizationTransition;
    if (!auth?.id) return null;
    return (
      <div className='flex items-center gap-0.5 justify-end'>
        <IconButton
          aria-label='Ver autorización'
          title='Ver detalles de la autorización'
          onClick={() => navigate(`/business/authorizations/${auth.id}`)}
          size='small'
        >
          <VisibilityIcon />
        </IconButton>
      </div>
    );
  };

  return (
    <>
      <div className='w-full p-5 flex flex-col bg-white rounded-lg shadow-md gap-5'>
        {isAuthorizationsLoading || sortedData?.length > 0 ? (
          <Table
            columns={columns}
            data={sortedData}
            renderActions={renderActions}
            isLoading={isAuthorizationsLoading}
            handleSort={handleSort}
            selectedSort={selectedSort}
            pagination={{
              totalItems,
              totalPages,
              currentPage,
              rowsPerPage: pageSize,
              onPageChange: handlePageChange,
              onRowsPerPageChange: handlePageSizeChange,
            }}
          />
        ) : (
          <div className='text-center py-8 text-gray-500 font-medium'>
            No hay solicitudes de autorización que coincidan con los filtros aplicados.
          </div>
        )}
      </div>
      <AuthorizationReasonModal
        reason={selectedReason}
        onClose={() => setSelectedReason(null)}
      />
    </>
  );
};

export default AuthorizationsTable;
